/**
 * Formats a phone number string into a standardized format
 * Handles various input formats and returns a consistently formatted phone number
 * 
 * @param phone - The phone number to format
 * @returns The formatted phone number
 */
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return '';
  
  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');

  // Handle international numbers (starting with +)
  if (phone.startsWith('+')) {
    // For international numbers, keep the original format but clean up spaces
    return phone.replace(/\s+/g, ' ').trim();
  }

  // Handle French numbers (10 digits)
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');
  }

  // If the number doesn't match expected formats, return it cleaned but otherwise unchanged
  return phone.trim();
};

/**
 * Formats a price string to ensure consistent display
 * 
 * @param price - The price to format
 * @returns The formatted price string
 */
export const formatPrice = (price: string): string => {
  if (!price) return '';
  return price.trim();
};

/**
 * Formats a date string according to the specified locale
 * 
 * @param date - The date to format
 * @param locale - The locale to use for formatting (defaults to 'fr-FR')
 * @returns The formatted date string
 */
export const formatDate = (date: string | Date, locale: string = 'fr-FR'): string => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString(locale);
};